<template>
  <div ref="root">
    <slot
      :aria-label="resolvedAriaLabel"
      name="control"
      :toggle-picker="togglePicker"
    >
      <span
        v-if="hideInput"
        class="selected-colour block cursor-pointer"
        data-testid="selected-color"
        :style="{ background: selectedColor || undefined }"
        @click="togglePicker"
      />

      <BaseInputText
        v-else
        v-model="selectedColor"
        :aria-label="resolvedAriaLabel"
        :clearable="clearable"
        data-testid="color-input"
        :disabled="disabled"
        :new-design="newDesign"
        :validations="validations"
        @click.stop.prevent="togglePicker"
      >
        <template #prepend>
          <div
            class="selected-colour block cursor-pointer"
            :class="{ disabled: disabled }"
            :style="{ background: selectedColor || undefined }"
            @click.stop.prevent="togglePicker"
          />
        </template>

        <template #append>
          <template v-if="newDesign">
            <BaseButton
              :aria-expanded="visible"
              aria-haspopup="dialog"
              :aria-label="togglePickerLabel"
              color="form"
              :disabled="disabled"
              :icon="dropdownIconName"
              new-design
              @click.stop.prevent="togglePicker"
            />
          </template>
        </template>
      </BaseInputText>
    </slot>

    <Teleport
      :disabled="!teleport"
      :to="teleport"
    >
      <Popper
        v-model="visible"
        click-outside-capture
        flip
        :root-element="root"
      >
        <div
          class="row q-pa-xs container bg-white"
          :class="{ 'color-selector': hasColorSelector }"
        >
          <QColor
            v-if="hasColorSelector"
            v-model="selectedColor"
            :default-view="defaultView"
            :disable="disabled"
            format-model="hex"
            :no-footer="hasPalette"
            :no-header="hasPalette"
            :palette="palette"
          />

          <template v-else>
            <button
              :aria-label="getText('clear_formatting')"
              class="color-tile tw:relative tw:cursor-pointer tw:appearance-none tw:bg-transparent tw:p-0"
              data-testid="clear-color-tile"
              type="button"
              @click="onColorSelected(null)"
            >
              <QIcon
                class="fit q-pb-xs"
                name="format_color_reset"
              >
                <BaseStreemTooltip>
                  {{ getText("clear_formatting") }}
                </BaseStreemTooltip>
              </QIcon>
            </button>

            <button
              v-for="color in colors"
              :key="color"
              :aria-label="getText('select_color_name', { color })"
              class="color-tile tw:cursor-pointer tw:appearance-none tw:p-0"
              data-testid="color-tile"
              :style="{ background: color }"
              type="button"
              @click="onColorSelected(color)"
            />

            <div
              v-if="customizeColor"
              class="no-wrap q-mt-xs flex items-baseline"
              data-testid="custom-color"
            >
              <BaseButton
                v-if="supportsEyedropper"
                class="q-mr-xs"
                new-design
                size="sm"
                @click="openEyedropper"
              >
                <QIcon name="colorize">
                  <BaseStreemTooltip>
                    {{ getText("select_color") }}
                  </BaseStreemTooltip>
                </QIcon>
              </BaseButton>

              <BaseInputText
                ref="customColorInput"
                class="flex-1"
                data-testid="custom-color-input"
                :model-value="customColor || undefined"
                :new-design="newDesign"
                placeholder="#ffffff"
                :validations="['isColor']"
                @submit="selectColor(selectedColor)"
                @update:model-value="onColorEntered"
              />
            </div>
          </template>
        </div>
      </Popper>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { useMutationObserver } from "@vueuse/core";
import { computed, ref, type TeleportProps, useTemplateRef, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import { getLocaleText } from "shared/boot/i18n";
import { BaseStreemTooltip } from "shared/components/base";
import BaseButton from "shared/components/base/BaseButton";
import BaseInputText from "shared/components/base/BaseInputText.vue";
import Popper from "shared/components/base/BasePopper";
import type { Validations } from "shared/composables/useValidation";
import type { Nullable } from "shared/types";

declare const window: Window & {
  EyeDropper: {
    new (): {
      open: () => Promise<{ sRGBHex: string }>;
    };
  };
};

interface ColorPickerProps {
  ariaLabel?: string;
  clearable?: boolean;
  customizeColor?: boolean;
  disabled?: boolean;
  hasColorSelector?: boolean;
  hideInput?: boolean;
  modelValue?: string;
  newDesign?: boolean;
  palette?: string[];
  teleport?: TeleportProps["to"];
  validations?: Validations;
}

const props = withDefaults(defineProps<ColorPickerProps>(), {
  ariaLabel: "",
  modelValue: "",
  teleport: null,
  validations: () => [],
  palette: () => [],
});

const emit = defineEmits(["update:modelValue"]);

const root = useTemplateRef("root");
const visible = ref(false);
const customColor = ref<Nullable<string>>("");
let colorPickerObserver: ReturnType<typeof useMutationObserver> | undefined;

watch(
  visible,
  (isVisible) => {
    if (isVisible) {
      const el =
        root.value?.querySelector<HTMLElement>(".q-color-picker") ?? null;

      if (el) {
        annotateColorPicker(el);

        colorPickerObserver = useMutationObserver(
          el,
          () => annotateColorPicker(el),
          { childList: true, subtree: true }
        );
      }
    } else {
      colorPickerObserver?.stop();
      colorPickerObserver = undefined;
    }
  },
  { flush: "post" }
);

const colors = [
  "rgb(255, 255, 255)",
  "rgb(0, 0, 0)",
  "rgb(230, 0, 0)",
  "rgb(255, 153, 0)",
  "rgb(255, 255, 0)",
  "rgb(0, 138, 0)",
  "rgb(0, 102, 204)",
  "rgb(153, 51, 255)",
  "rgb(250, 204, 204)",
  "rgb(255, 235, 204)",
  "rgb(255, 255, 204)",
  "rgb(204, 232, 204)",
  "rgb(204, 224, 245)",
  "rgb(235, 214, 255)",
  "rgb(187, 187, 187)",
  "rgb(240, 102, 102)",
  "rgb(255, 194, 102)",
  "rgb(255, 255, 102)",
  "rgb(102, 185, 102)",
  "rgb(102, 163, 224)",
  "rgb(194, 133, 255)",
  "rgb(136, 136, 136)",
  "rgb(161, 0, 0)",
  "rgb(178, 107, 0)",
  "rgb(178, 178, 0)",
  "rgb(0, 97, 0)",
  "rgb(0, 71, 178)",
  "rgb(107, 36, 178)",
  "rgb(68, 68, 68)",
  "rgb(92, 0, 0)",
  "rgb(102, 61, 0)",
  "rgb(102, 102, 0)",
  "rgb(0, 55, 0)",
  "rgb(0, 41, 102)",
  "rgb(61, 20, 102)",
];

const customColorInput = ref<ComponentExposed<typeof BaseInputText<"text">>>();

const dropdownIconName = computed(() =>
  visible.value ? "keyboard_arrow_up" : "keyboard_arrow_down"
);

const resolvedAriaLabel = computed<string>(
  () => props.ariaLabel || getText("default_label")
);

const togglePickerLabel = computed<string>(() => getText("toggle_picker"));

const selectedColor = computed({
  get() {
    return props.modelValue || "";
  },
  set(value: Nullable<string>) {
    emit("update:modelValue", value);
  },
});

const hasPalette = computed(() => props.palette.length > 0);

const defaultView = computed(() => {
  if (hasPalette.value) {
    return "palette";
  }

  return "spectrum";
});

const supportsEyedropper = computed(
  () => typeof window !== "undefined" && "EyeDropper" in window
);

const tabLabels = computed<string[]>(() => [
  getText("color_picker_tab"),
  getText("rgb_sliders_tab"),
  getText("color_grid_tab"),
]);

function getText(key: string, params?: Record<string, unknown>): string {
  return getLocaleText(`color_picker.${key}`, params);
}

function togglePicker() {
  if (!props.disabled) {
    visible.value = !visible.value;
  }
}

function selectColor(color: Nullable<string>) {
  selectedColor.value = color;
  visible.value = false;
}

function onColorSelected(color: Nullable<string>) {
  selectColor(color);
  customColor.value = "";
}

async function openEyedropper() {
  const eyeDropper = new window.EyeDropper();
  const result = await eyeDropper.open();
  customColor.value = result.sRGBHex;
  selectColor(customColor.value);
}

function onColorEntered(color: Nullable<string>) {
  customColor.value = color;

  if (customColorInput.value?.validate()) {
    selectedColor.value = customColor.value;
  }
}

function annotateColorPicker(pickerRoot: HTMLElement) {
  pickerRoot
    .querySelectorAll<HTMLElement>(".q-tab[role='tab']")
    .forEach((tab, index) => {
      tab.setAttribute(
        "aria-label",
        tabLabels.value[index] ?? getText("color_picker_tab")
      );
    });

  pickerRoot
    .querySelector<HTMLElement>(".q-color-picker__hue.q-slider")
    ?.setAttribute("aria-label", getText("hue_slider"));

  pickerRoot
    .querySelectorAll<HTMLInputElement>(
      ".q-color-picker__header-banner input.fit"
    )
    .forEach((input) => {
      input.setAttribute("aria-label", getText("hex_input"));
    });

  pickerRoot
    .querySelectorAll<HTMLElement>(".q-color-picker__tune-tab .row")
    .forEach((row) => {
      const label = getRGBChannelLabel(
        row.firstElementChild?.textContent?.trim() ?? ""
      );

      if (!label) {
        return;
      }

      row
        .querySelector<HTMLElement>(".q-slider")
        ?.setAttribute("aria-label", label);

      row
        .querySelector<HTMLInputElement>("input")
        ?.setAttribute("aria-label", label);
    });
}

function getRGBChannelLabel(channel: string): string {
  switch (channel) {
    case "R":
      return getText("rgb_red");
    case "G":
      return getText("rgb_green");
    case "B":
      return getText("rgb_blue");
    default:
      return "";
  }
}
</script>

<style lang="scss" scoped>
.selected-colour {
  min-width: 12px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid $control-border;
}

.container {
  background: $white;
  border: 1px solid $secondary;
  padding: 8px;
  border-radius: 3px;
  box-shadow: 0 0 8px rgb(0 0 0 / 20%);
  width: 138px;

  &.color-selector {
    padding: 0;
  }

  .color-tile {
    width: 16px;
    height: 16px;
    margin: 2px;
    border-radius: 2px;
    border: 1px solid transparent;

    &:hover {
      border-color: $primary;
    }
  }
}
</style>
